import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { UserPlus, Mail, Shield, Trash2, X } from "lucide-react";

interface User {
  id: string;
  name: string;
  email: string;
  role: "admin" | "user" | "viewer";
  status: "active" | "pending" | "suspended";
  last_login: string | null;
  created_at: string;
}

export function Users() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<User["role"]>("user");

  // TODO: Replace with actual API call when backend is ready
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-users"],
    queryFn: async () => {
      // Mock data for now - will be replaced with api.getMyUsers()
      return {
        users: [] as User[],
      };
    },
  });

  const handleInviteUser = async () => {
    // TODO: Implement invite user API call
    console.log("Inviting user:", inviteEmail, inviteRole);
    setShowInviteModal(false);
    setInviteEmail("");
    setInviteRole("user");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-400">
          Error loading users: {(error as Error).message}
        </div>
      </div>
    );
  }

  const users = data?.users || [];

  const getRoleBadge = (role: User["role"]) => {
    const colors = {
      admin: "bg-red-500/10 text-red-400 border-red-500/20",
      user: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      viewer: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    };
    return colors[role];
  };

  const getStatusBadge = (status: User["status"]) => {
    const colors = {
      active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      suspended: "bg-red-500/10 text-red-400 border-red-500/20",
    };
    return colors[status];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Users</h1>
          <p className="mt-1 text-sm text-slate-400">
            Manage user access to your instances
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
        >
          <UserPlus className="h-5 w-5" />
          Invite User
        </button>
      </div>

      {/* User Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="text-sm text-slate-400 mb-1">Total Users</div>
          <div className="text-3xl font-bold text-slate-100">
            {users.length}
          </div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="text-sm text-slate-400 mb-1">Active</div>
          <div className="text-3xl font-bold text-emerald-400">
            {users.filter((u) => u.status === "active").length}
          </div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="text-sm text-slate-400 mb-1">Pending</div>
          <div className="text-3xl font-bold text-yellow-400">
            {users.filter((u) => u.status === "pending").length}
          </div>
        </div>
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="text-sm text-slate-400 mb-1">Admins</div>
          <div className="text-3xl font-bold text-red-400">
            {users.filter((u) => u.role === "admin").length}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 overflow-hidden">
        {users.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Mail className="mx-auto h-12 w-12 text-slate-600" />
            <h3 className="mt-2 text-sm font-semibold text-slate-300">
              No users yet
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Invite team members to collaborate on your instances
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
            >
              <UserPlus className="h-5 w-5" />
              Invite User
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/30 border-b border-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-slate-700/20 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-slate-100">
                          {user.name}
                        </div>
                        <div className="text-sm text-slate-400">
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border capitalize ${getRoleBadge(user.role)}`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border capitalize ${getStatusBadge(user.status)}`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString()
                        : "Never"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="text-slate-400 hover:text-red-400 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-100">
                Invite User
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-slate-400 hover:text-slate-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) =>
                    setInviteRole(e.target.value as User["role"])
                  }
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-blue-500"
                >
                  <option value="viewer">Viewer - Read-only access</option>
                  <option value="user">
                    User - Can manage BoBs and instances
                  </option>
                  <option value="admin">Admin - Full access</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleInviteUser}
                  disabled={!inviteEmail}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Send Invite
                </button>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
